import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, Search, Calendar, Clock, Users, MapPin, Loader2, Trash2,
  MessageSquare, CheckCircle, ListTodo, User, Building2, ChevronRight,
  FileText, PlayCircle, XCircle, Check, Download, Save, X, ChevronDown, Edit
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const MEETING_STATUS = {
  'Draft': { color: 'bg-gray-100 text-gray-800', icon: FileText },
  'Scheduled': { color: 'bg-blue-100 text-blue-800', icon: Calendar },
  'In Progress': { color: 'bg-amber-100 text-amber-800', icon: PlayCircle },
  'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'Cancelled': { color: 'bg-red-100 text-red-800', icon: XCircle },
};

const ACTION_STATUS = {
  'Pending': { color: 'bg-slate-100 text-slate-800' },
  'In Progress': { color: 'bg-blue-100 text-blue-800' },
  'Completed': { color: 'bg-green-100 text-green-800' },
  'Overdue': { color: 'bg-red-100 text-red-800' },
};

const MeetingPad = () => {
  const [meetings, setMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showMyMeetings, setShowMyMeetings] = useState(false);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showMeetingPadModal, setShowMeetingPadModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Create form data
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    date_time: '',
    agendas: [],
    participants: [],
    external_participants: [],
  });
  
  // Edit form data (separate from create)
  const [editData, setEditData] = useState({
    title: '',
    location: '',
    date_time: '',
    participants: [],
    external_participants: [],
  });
  const [editParticipantSearch, setEditParticipantSearch] = useState('');
  const [editParticipantPopoverOpen, setEditParticipantPopoverOpen] = useState(false);
  const [editNewExternal, setEditNewExternal] = useState({ name: '', email: '', organization: '' });
  
  const [newAgendaText, setNewAgendaText] = useState('');
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantPopoverOpen, setParticipantPopoverOpen] = useState(false);
  
  // External participant form
  const [newExternal, setNewExternal] = useState({ name: '', email: '', organization: '' });
  
  // Meeting Pad inputs
  const [newDecision, setNewDecision] = useState('');
  const [newAction, setNewAction] = useState({ content: '', assigned_to_id: '', assigned_to_name: '', due_date: '' });
  const [actionAssigneeOpen, setActionAssigneeOpen] = useState(false);
  
  const { getAuthHeaders, currentOffice, user, offices } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Helper to get office ID
  const getOfficeId = useCallback(() => {
    if (currentOffice?.id) return currentOffice.id;
    const storedOffice = localStorage.getItem('current_office');
    if (storedOffice) {
      try {
        return JSON.parse(storedOffice)?.id;
      } catch (e) {
        console.error('Error parsing stored office:', e);
      }
    }
    if (offices && offices.length > 0) {
      return offices[0].id;
    }
    return null;
  }, [currentOffice?.id, offices]);
  
  // Helper to get headers
  const getRequestHeaders = useCallback(() => {
    const headers = getAuthHeaders();
    const officeId = getOfficeId();
    if (officeId) {
      headers['X-Office-ID'] = officeId;
    }
    return headers;
  }, [getAuthHeaders, getOfficeId]);

  // Fetch meetings
  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getRequestHeaders();
      let url = `${API_URL}/api/meetings?`;
      if (filterStatus && filterStatus !== 'all') {
        url += `status=${encodeURIComponent(filterStatus)}&`;
      }
      if (showMyMeetings) {
        url += 'my_meetings=true&';
      }
      
      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setMeetings(data);
      } else {
        toast.error('Failed to load meetings');
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [API_URL, filterStatus, showMyMeetings, getRequestHeaders]);
  
  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/employees/list`, { headers });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, [API_URL, getRequestHeaders]);

  useEffect(() => {
    fetchMeetings();
    fetchEmployees();
  }, [fetchMeetings, fetchEmployees]);

  // Add agenda item
  const addAgendaItem = () => {
    if (!newAgendaText.trim()) return;
    setFormData({
      ...formData,
      agendas: [...formData.agendas, { content: newAgendaText.trim() }]
    });
    setNewAgendaText('');
  };
  
  // Remove agenda item
  const removeAgendaItem = (index) => {
    setFormData({
      ...formData,
      agendas: formData.agendas.filter((_, i) => i !== index)
    });
  };
  
  // Toggle participant
  const toggleParticipant = (employee) => {
    const exists = formData.participants.some(p => p.employee_id === employee.employee_id);
    if (exists) {
      setFormData({
        ...formData,
        participants: formData.participants.filter(p => p.employee_id !== employee.employee_id)
      });
    } else {
      setFormData({
        ...formData,
        participants: [...formData.participants, employee]
      });
    }
  };
  
  // Add external participant
  const addExternalParticipant = () => {
    if (!newExternal.name.trim()) return;
    setFormData({
      ...formData,
      external_participants: [...formData.external_participants, { ...newExternal }]
    });
    setNewExternal({ name: '', email: '', organization: '' });
  };
  
  // Remove external participant
  const removeExternalParticipant = (index) => {
    setFormData({
      ...formData,
      external_participants: formData.external_participants.filter((_, i) => i !== index)
    });
  };
  
  const resetForm = () => {
    setFormData({
      title: '',
      location: '',
      date_time: '',
      agendas: [],
      participants: [],
      external_participants: [],
    });
    setNewAgendaText('');
    setNewExternal({ name: '', email: '', organization: '' });
  };

  // Start a meeting (instant)
  const handleStartMeeting = async () => {
    if (!formData.title) {
      toast.error('Meeting title is required');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          is_scheduled: false,
        }),
      });
      
      if (response.ok) {
        const meeting = await response.json();
        toast.success('Meeting started!');
        setShowCreateModal(false);
        resetForm();
        fetchMeetings();
        // Open the meeting pad
        setSelectedMeeting(meeting);
        setShowMeetingPadModal(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start meeting');
      }
    } catch (error) {
      console.error('Start meeting error:', error);
      toast.error('Failed to start meeting');
    } finally {
      setSaving(false);
    }
  };
  
  // Schedule a meeting
  const handleScheduleMeeting = async () => {
    if (!formData.title) {
      toast.error('Meeting title is required');
      return;
    }
    if (!formData.date_time) {
      toast.error('Date and time is required');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          is_scheduled: true,
        }),
      });
      
      if (response.ok) {
        toast.success('Meeting scheduled!');
        setShowScheduleModal(false);
        setShowCreateModal(false);
        resetForm();
        fetchMeetings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to schedule meeting');
      }
    } catch (error) {
      console.error('Schedule meeting error:', error);
      toast.error('Failed to schedule meeting');
    } finally {
      setSaving(false);
    }
  };

  // Open meeting pad
  const openMeetingPad = async (meetingId) => {
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${meetingId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSelectedMeeting(data);
        setIsEditMode(false);
        setShowMeetingPadModal(true);
      }
    } catch (error) {
      console.error('Error fetching meeting:', error);
      toast.error('Failed to load meeting');
    }
  };
  
  // Enter edit mode for draft/scheduled meetings
  const enterEditMode = () => {
    if (!selectedMeeting) return;
    setEditData({
      title: selectedMeeting.title || '',
      location: selectedMeeting.location || '',
      date_time: selectedMeeting.date_time ? selectedMeeting.date_time.slice(0, 16) : '',
      participants: selectedMeeting.participants || [],
      external_participants: selectedMeeting.external_participants || [],
    });
    setEditNewExternal({ name: '', email: '', organization: '' });
    setIsEditMode(true);
  };
  
  // Cancel edit mode
  const cancelEditMode = () => {
    setIsEditMode(false);
    setEditData({
      title: '',
      location: '',
      date_time: '',
      participants: [],
      external_participants: [],
    });
  };
  
  // Toggle participant in edit mode
  const toggleEditParticipant = (employee) => {
    const exists = editData.participants.some(p => p.employee_id === employee.employee_id);
    if (exists) {
      setEditData({
        ...editData,
        participants: editData.participants.filter(p => p.employee_id !== employee.employee_id)
      });
    } else {
      setEditData({
        ...editData,
        participants: [...editData.participants, employee]
      });
    }
  };
  
  // Add external participant in edit mode
  const addEditExternalParticipant = () => {
    if (!editNewExternal.name.trim()) return;
    setEditData({
      ...editData,
      external_participants: [...editData.external_participants, { ...editNewExternal }]
    });
    setEditNewExternal({ name: '', email: '', organization: '' });
  };
  
  // Remove external participant in edit mode
  const removeEditExternalParticipant = (index) => {
    setEditData({
      ...editData,
      external_participants: editData.external_participants.filter((_, i) => i !== index)
    });
  };
  
  // Save meeting edits
  const saveEditChanges = async () => {
    if (!editData.title.trim()) {
      toast.error('Meeting title is required');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          title: editData.title,
          location: editData.location,
          date_time: editData.date_time || null,
          participants: editData.participants,
          external_participants: editData.external_participants,
        }),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedMeeting(updated);
        setIsEditMode(false);
        toast.success('Meeting updated');
        fetchMeetings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update meeting');
      }
    } catch (error) {
      console.error('Update meeting error:', error);
      toast.error('Failed to update meeting');
    } finally {
      setSaving(false);
    }
  };
  
  // Check if user can edit meeting (organizer or admin, and meeting is draft/scheduled)
  const canEditMeeting = () => {
    if (!selectedMeeting || !user) return false;
    const isOrganizer = selectedMeeting.organizer_id === user.id;
    const isAdmin = user.roles?.some(r => r.role === 'super_admin' || r.role === 'office_admin');
    const isEditable = ['Draft', 'Scheduled', 'In Progress', 'Completed'].includes(selectedMeeting.status);
    return (isOrganizer || isAdmin) && isEditable;
  };
  
  // Check if user can delete meeting (organizer or admin)
  const canDeleteMeeting = () => {
    if (!selectedMeeting || !user) return false;
    const isOrganizer = selectedMeeting.organizer_id === user.id;
    const isAdmin = user.roles?.some(r => r.role === 'super_admin' || r.role === 'office_admin');
    return isOrganizer || isAdmin;
  };
  
  // Update agenda notes - debounced to avoid too many API calls
  const [agendaNotes, setAgendaNotes] = useState({});
  const notesTimeoutRef = useRef({});
  
  // Initialize agenda notes when meeting is loaded
  useEffect(() => {
    if (selectedMeeting?.agendas) {
      const notes = {};
      selectedMeeting.agendas.forEach(a => {
        notes[a.id] = a.notes || '';
      });
      setAgendaNotes(notes);
    }
  }, [selectedMeeting?.id, selectedMeeting?.agendas]);
  
  const handleAgendaNotesChange = (agendaId, value) => {
    // Update local state immediately for responsive UI
    setAgendaNotes(prev => ({ ...prev, [agendaId]: value }));
    
    // Clear existing timeout for this agenda
    if (notesTimeoutRef.current[agendaId]) {
      clearTimeout(notesTimeoutRef.current[agendaId]);
    }
    
    // Debounce the API call
    notesTimeoutRef.current[agendaId] = setTimeout(() => {
      saveAgendaNotes(agendaId, value);
    }, 1000); // Save after 1 second of no typing
  };
  
  const saveAgendaNotes = async (agendaId, notes) => {
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}/agendas/${agendaId}/notes`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ notes }),
      });
      
      if (response.ok) {
        // Silently update - don't show toast for auto-save
        const updated = await response.json();
        // Update the selected meeting but preserve local notes state
        setSelectedMeeting(prev => ({
          ...updated,
        }));
      }
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };
  
  // Save notes on blur (when user clicks away)
  const handleAgendaNotesBlur = (agendaId) => {
    // Clear any pending timeout
    if (notesTimeoutRef.current[agendaId]) {
      clearTimeout(notesTimeoutRef.current[agendaId]);
    }
    // Save immediately
    saveAgendaNotes(agendaId, agendaNotes[agendaId] || '');
  };
  
  // Add decision
  const handleAddDecision = async () => {
    if (!newDecision.trim()) return;
    
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}/decisions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content: newDecision }),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedMeeting(updated);
        setNewDecision('');
        toast.success('Decision recorded');
      }
    } catch (error) {
      toast.error('Failed to add decision');
    } finally {
      setSaving(false);
    }
  };
  
  // Add action
  const handleAddAction = async () => {
    if (!newAction.content.trim()) return;
    
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}/actions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newAction),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedMeeting(updated);
        setNewAction({ content: '', assigned_to_id: '', assigned_to_name: '', due_date: '' });
        toast.success('Action added & task created');
      }
    } catch (error) {
      toast.error('Failed to add action');
    } finally {
      setSaving(false);
    }
  };
  
  // Update action status
  const handleUpdateActionStatus = async (actionId, newStatus) => {
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}/actions/${actionId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedMeeting(updated);
        toast.success('Action updated');
        fetchMeetings();
      }
    } catch (error) {
      toast.error('Failed to update action');
    } finally {
      setSaving(false);
    }
  };
  
  // Save as draft
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}/save-draft`, {
        method: 'POST',
        headers,
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedMeeting(updated);
        toast.success('Meeting saved as draft');
        fetchMeetings();
      }
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };
  
  // Complete meeting
  const handleCompleteMeeting = async () => {
    setSaving(true);
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}/complete`, {
        method: 'POST',
        headers,
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedMeeting(updated);
        toast.success('Meeting completed! Notes generated.');
        fetchMeetings();
      }
    } catch (error) {
      toast.error('Failed to complete meeting');
    } finally {
      setSaving(false);
    }
  };
  
  // Download meeting notes as PDF
  const downloadMeetingNotes = async () => {
    if (!selectedMeeting?.id) return;
    
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${selectedMeeting.id}/download-pdf`, {
        headers,
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `meeting-notes-${selectedMeeting.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Meeting notes PDF downloaded');
      } else {
        toast.error('Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download meeting notes');
    }
  };
  
  // Delete meeting
  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    
    try {
      const headers = getRequestHeaders();
      const response = await fetch(`${API_URL}/api/meetings/${meetingId}`, {
        method: 'DELETE',
        headers,
      });
      
      if (response.ok) {
        toast.success('Meeting deleted');
        setShowMeetingPadModal(false);
        fetchMeetings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete meeting');
      }
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };
  
  // Format date with timezone
  const formatDateTime = (dateStr, tz) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const options = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: tz || undefined
      };
      const formatted = date.toLocaleString('en-US', options);
      // Add timezone abbreviation if available
      if (tz) {
        const tzAbbr = date.toLocaleString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop();
        return `${formatted} (${tzAbbr})`;
      }
      return formatted;
    } catch (e) {
      // Fallback without timezone
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };
  
  // Filter meetings
  const filteredMeetings = meetings.filter(m => 
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.organizer_name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Filter employees for search
  const filteredEmployees = employees.filter(emp =>
    emp.employee_name.toLowerCase().includes(participantSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meeting-pad-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold">Meeting Pad</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCreateModal(true)} data-testid="new-meeting-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Meeting
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search meetings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Meetings List */}
      <div className="grid gap-4">
        {filteredMeetings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No meetings found</h3>
              <p className="text-gray-500 mt-1">Start or schedule your first meeting</p>
              <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                New Meeting
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredMeetings.map((meeting) => {
            const StatusIcon = MEETING_STATUS[meeting.status]?.icon || Calendar;
            return (
              <Card 
                key={meeting.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openMeetingPad(meeting.id)}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{meeting.title}</h3>
                        <Badge className={MEETING_STATUS[meeting.status]?.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {meeting.status}
                        </Badge>
                        {meeting.status === 'Completed' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={async (e) => { 
                              e.stopPropagation(); 
                              // Direct PDF download
                              try {
                                const headers = getRequestHeaders();
                                const response = await fetch(`${API_URL}/api/meetings/${meeting.id}/download-pdf`, { headers });
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `meeting-notes-${meeting.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  URL.revokeObjectURL(url);
                                  toast.success('PDF downloaded');
                                }
                              } catch (error) {
                                toast.error('Failed to download PDF');
                              }
                            }}
                            title="Download meeting notes PDF"
                          >
                            <Download className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDateTime(meeting.date_time, meeting.timezone)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {meeting.organizer_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {meeting.participant_count} participants
                        </span>
                        {meeting.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {meeting.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {meeting.pending_actions > 0 && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          <ListTodo className="h-3 w-3 mr-1" />
                          {meeting.pending_actions} pending
                        </Badge>
                      )}
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      
      {/* Create Meeting Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              New Meeting
            </DialogTitle>
            <DialogDescription>Set up your meeting details and agenda</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Meeting Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter meeting title"
              />
            </div>
            
            <div>
              <Label>Location (Optional)</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Meeting room or video link"
              />
            </div>
            
            {/* Meeting Agendas */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <ListTodo className="h-4 w-4" />
                Meeting Agendas
              </Label>
              <div className="space-y-2">
                {formData.agendas.map((agenda, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">
                      {idx + 1}
                    </span>
                    <span className="flex-1 text-sm">{agenda.content}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeAgendaItem(idx)}>
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    placeholder="Add agenda item..."
                    value={newAgendaText}
                    onChange={(e) => setNewAgendaText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addAgendaItem()}
                  />
                  <Button variant="outline" onClick={addAgendaItem}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Participants */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4" />
                Select Participants
              </Label>
              <Popover open={participantPopoverOpen} onOpenChange={setParticipantPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    {formData.participants.length > 0 
                      ? `${formData.participants.length} participant(s) selected`
                      : "Select participants..."
                    }
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search employees..." 
                      value={participantSearch}
                      onValueChange={setParticipantSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No employees found.</CommandEmpty>
                      <CommandGroup>
                        {filteredEmployees.map((emp) => {
                          const isSelected = formData.participants.some(p => p.employee_id === emp.employee_id);
                          return (
                            <CommandItem
                              key={emp.employee_id}
                              onSelect={() => toggleParticipant(emp)}
                              className="cursor-pointer"
                            >
                              <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              {emp.employee_name}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {formData.participants.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.participants.map((p, i) => (
                    <Badge key={i} variant="secondary" className="flex items-center gap-1">
                      {p.employee_name}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => toggleParticipant(p)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* External Participants */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4" />
                External Participants
              </Label>
              <div className="space-y-2">
                {formData.external_participants.map((ext, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <span className="flex-1 text-sm">{ext.name} {ext.organization && `(${ext.organization})`}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeExternalParticipant(index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Name"
                    value={newExternal.name}
                    onChange={(e) => setNewExternal({ ...newExternal, name: e.target.value })}
                  />
                  <Input
                    placeholder="Email (optional)"
                    value={newExternal.email}
                    onChange={(e) => setNewExternal({ ...newExternal, email: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="Organization"
                      value={newExternal.organization}
                      onChange={(e) => setNewExternal({ ...newExternal, organization: e.target.value })}
                    />
                    <Button variant="outline" onClick={addExternalParticipant}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button 
              variant="outline"
              onClick={() => { setShowCreateModal(false); setShowScheduleModal(true); }}
              disabled={saving}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule Meeting
            </Button>
            <Button onClick={handleStartMeeting} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              Start Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Schedule Meeting Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Schedule Meeting
            </DialogTitle>
            <DialogDescription>Choose when to hold this meeting</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                value={formData.date_time}
                onChange={(e) => setFormData({ ...formData, date_time: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowScheduleModal(false); setShowCreateModal(true); }}>Back</Button>
            <Button onClick={handleScheduleMeeting} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calendar className="h-4 w-4 mr-2" />}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Meeting Pad Modal */}
      <Dialog open={showMeetingPadModal} onOpenChange={(open) => { setShowMeetingPadModal(open); if (!open) setIsEditMode(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedMeeting && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {isEditMode ? (
                      <div className="space-y-3">
                        <div>
                          <Label>Meeting Title</Label>
                          <Input
                            value={editData.title}
                            onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                            placeholder="Meeting title"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Date & Time</Label>
                            <Input
                              type="datetime-local"
                              value={editData.date_time}
                              onChange={(e) => setEditData({ ...editData, date_time: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label>Location</Label>
                            <Input
                              value={editData.location}
                              onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                              placeholder="Meeting room or video link"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <DialogTitle className="text-xl flex items-center gap-2">
                          {selectedMeeting.title}
                          {canEditMeeting() && (
                            <Button size="sm" variant="ghost" onClick={enterEditMode} title="Edit meeting details">
                              <Edit className="h-4 w-4 text-gray-500" />
                            </Button>
                          )}
                          {selectedMeeting.status === 'Completed' && selectedMeeting.meeting_notes && (
                            <Button size="sm" variant="ghost" onClick={downloadMeetingNotes} title="Download meeting notes">
                              <Download className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                        </DialogTitle>
                        <DialogDescription className="mt-1">
                          <div className="flex flex-wrap gap-3 text-sm">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDateTime(selectedMeeting.date_time, selectedMeeting.timezone)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {selectedMeeting.organizer_name}
                            </span>
                            {selectedMeeting.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {selectedMeeting.location}
                              </span>
                            )}
                          </div>
                        </DialogDescription>
                      </>
                    )}
                  </div>
                  {!isEditMode && (
                    <Badge className={MEETING_STATUS[selectedMeeting.status]?.color}>
                      {selectedMeeting.status}
                    </Badge>
                  )}
                </div>
              </DialogHeader>
              
              {/* Edit Mode: Participants */}
              {isEditMode && (
                <div className="space-y-4 border-t pt-4">
                  {/* Participants */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4" />
                      Participants
                    </Label>
                    <Popover open={editParticipantPopoverOpen} onOpenChange={setEditParticipantPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between">
                          {editData.participants.length > 0 
                            ? `${editData.participants.length} participant(s) selected`
                            : "Select participants..."
                          }
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Search employees..." 
                            value={editParticipantSearch}
                            onValueChange={setEditParticipantSearch}
                          />
                          <CommandList>
                            <CommandEmpty>No employees found.</CommandEmpty>
                            <CommandGroup>
                              {employees.filter(emp => 
                                emp.employee_name.toLowerCase().includes(editParticipantSearch.toLowerCase())
                              ).map((emp) => {
                                const isSelected = editData.participants.some(p => p.employee_id === emp.employee_id);
                                return (
                                  <CommandItem
                                    key={emp.employee_id}
                                    onSelect={() => toggleEditParticipant(emp)}
                                    className="cursor-pointer"
                                  >
                                    <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                      {isSelected && <Check className="h-3 w-3 text-white" />}
                                    </div>
                                    {emp.employee_name}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {editData.participants.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {editData.participants.map((p, i) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1">
                            {p.employee_name}
                            <X className="h-3 w-3 cursor-pointer" onClick={() => toggleEditParticipant(p)} />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* External Participants */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4" />
                      External Participants
                    </Label>
                    <div className="space-y-2">
                      {editData.external_participants.map((ext, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                          <span className="flex-1 text-sm">{ext.name} {ext.organization && `(${ext.organization})`}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeEditExternalParticipant(index)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          placeholder="Name"
                          value={editNewExternal.name}
                          onChange={(e) => setEditNewExternal({ ...editNewExternal, name: e.target.value })}
                        />
                        <Input
                          placeholder="Email (optional)"
                          value={editNewExternal.email}
                          onChange={(e) => setEditNewExternal({ ...editNewExternal, email: e.target.value })}
                        />
                        <div className="flex gap-2">
                          <Input
                            placeholder="Organization"
                            value={editNewExternal.organization}
                            onChange={(e) => setEditNewExternal({ ...editNewExternal, organization: e.target.value })}
                          />
                          <Button variant="outline" onClick={addEditExternalParticipant}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Edit Mode Actions */}
                  <div className="flex gap-2 justify-end pt-2">
                    <Button variant="outline" onClick={cancelEditMode}>Cancel</Button>
                    <Button onClick={saveEditChanges} disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Participants (View Mode) */}
              {!isEditMode && (selectedMeeting.participants?.length > 0 || selectedMeeting.external_participants?.length > 0) && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Attendees ({selectedMeeting.participants.length + selectedMeeting.external_participants.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMeeting.participants.map((p, i) => (
                      <Badge key={i} variant="outline">{p.employee_name}</Badge>
                    ))}
                    {selectedMeeting.external_participants.map((p, i) => (
                      <Badge key={`ext-${i}`} variant="secondary">
                        {p.name} {p.organization && `(${p.organization})`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Meeting Pad Tabs - Only show when not in edit mode */}
              {!isEditMode && (
                <Tabs defaultValue="discussion" className="mt-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="discussion" className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" />
                      Discussion
                    </TabsTrigger>
                    <TabsTrigger value="decisions" className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Decisions ({selectedMeeting.decisions?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="actions" className="flex items-center gap-1">
                      <ListTodo className="h-4 w-4" />
                      Actions ({selectedMeeting.actions?.length || 0})
                    </TabsTrigger>
                  </TabsList>
                
                {/* Discussion Tab - Agendas with notes */}
                <TabsContent value="discussion" className="space-y-4">
                  {selectedMeeting.agendas?.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No agenda items</p>
                  ) : (
                    selectedMeeting.agendas?.map((agenda) => (
                      <div key={agenda.id} className="border rounded-lg p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                            {agenda.number}
                          </span>
                          <h4 className="font-medium flex-1">{agenda.content}</h4>
                        </div>
                        <Textarea
                          placeholder="Add discussion notes for this agenda item..."
                          value={agendaNotes[agenda.id] ?? agenda.notes ?? ''}
                          onChange={(e) => handleAgendaNotesChange(agenda.id, e.target.value)}
                          onBlur={() => handleAgendaNotesBlur(agenda.id)}
                          rows={3}
                          className="text-sm"
                          disabled={selectedMeeting.status === 'Completed'}
                        />
                      </div>
                    ))
                  )}
                </TabsContent>
                
                {/* Decisions Tab */}
                <TabsContent value="decisions" className="space-y-4">
                  {selectedMeeting.status !== 'Completed' && (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Record a decision..."
                        value={newDecision}
                        onChange={(e) => setNewDecision(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddDecision()}
                      />
                      <Button onClick={handleAddDecision} disabled={saving}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedMeeting.decisions?.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No decisions recorded yet</p>
                    ) : (
                      selectedMeeting.decisions?.map((d, i) => (
                        <div key={d.id || i} className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-lg">
                          <p className="text-sm font-medium">{d.content}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            by {d.made_by_name} • {new Date(d.made_at).toLocaleString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>
                
                {/* Actions Tab */}
                <TabsContent value="actions" className="space-y-4">
                  {selectedMeeting.status !== 'Completed' && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Input
                        placeholder="Action item..."
                        value={newAction.content}
                        onChange={(e) => setNewAction({ ...newAction, content: e.target.value })}
                        className="md:col-span-2"
                      />
                      <Popover open={actionAssigneeOpen} onOpenChange={setActionAssigneeOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="justify-between">
                            {newAction.assigned_to_name || "Assign to (self)"}
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                          <Command>
                            <CommandInput placeholder="Search..." />
                            <CommandList>
                              <CommandEmpty>No one found.</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  onSelect={() => {
                                    setNewAction({ ...newAction, assigned_to_id: '', assigned_to_name: '' });
                                    setActionAssigneeOpen(false);
                                  }}
                                >
                                  <User className="h-4 w-4 mr-2" />
                                  Assign to myself
                                </CommandItem>
                                {employees.map((emp) => (
                                  <CommandItem
                                    key={emp.employee_id}
                                    onSelect={() => {
                                      setNewAction({
                                        ...newAction,
                                        assigned_to_id: emp.employee_id,
                                        assigned_to_name: emp.employee_name
                                      });
                                      setActionAssigneeOpen(false);
                                    }}
                                  >
                                    {emp.employee_name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={newAction.due_date}
                          onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
                        />
                        <Button onClick={handleAddAction} disabled={saving}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedMeeting.actions?.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No action items yet</p>
                    ) : (
                      selectedMeeting.actions?.map((a, i) => (
                        <div key={a.id || i} className="border p-3 rounded-lg flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{a.content}</p>
                            <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                              {a.assigned_to_name && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {a.assigned_to_name}
                                </span>
                              )}
                              {a.due_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Due: {a.due_date}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={ACTION_STATUS[a.status]?.color}>{a.status}</Badge>
                            {a.status !== 'Completed' && selectedMeeting.status !== 'Completed' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUpdateActionStatus(a.id, 'Completed')}
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
              )}
              
              {!isEditMode && (
                <DialogFooter className="flex gap-2 flex-wrap mt-4">
                  <Button variant="outline" onClick={() => setShowMeetingPadModal(false)}>Close</Button>
                  {canDeleteMeeting() && (
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleDeleteMeeting(selectedMeeting.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                  {selectedMeeting.status === 'Completed' && (
                    <Button variant="outline" onClick={downloadMeetingNotes} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  )}
                  {selectedMeeting.status !== 'Completed' && (
                    <>
                      <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
                        <Save className="h-4 w-4 mr-2" />
                        Save as Draft
                      </Button>
                      <Button onClick={handleCompleteMeeting} disabled={saving} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Complete Meeting
                      </Button>
                    </>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingPad;
